import { ListPayload } from 'core/http/response';
import { Customer } from 'store/customers/customer';
import { Individual } from 'store/individuals/individual';
import { SecurityCheckStatus } from 'types/common';
import { SortDirection } from 'utils/tableHelpers';
import { EmailStatusResponse } from './types';

export enum JobStatuses {
    NEW = 'NEW',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    PROPOSED = 'PROPOSED',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
    CLOSED = 'CLOSED',
    AWAITING_PAYMENT = 'AWAITING_PAYMENT',
    PARTIALLY_PAID = 'PARTIALLY_PAID',
    OVERDUE = 'OVERDUE',
}

export enum GetJobsSortParams {
    ID = 'ID',
    STATUS = 'STATUS',
    CREATED_AT = 'CREATED_AT',
}

export interface GetJobsParams {
    sortDirection?: SortDirection;
    sort?: GetJobsSortParams;
    page?: number;
    size?: number;
    customerId?: string;
    brokerId?: string;
    ownerId?: string;
    teamId?: string;
    manifestIds?: string[];
    driverId?: string;
    tags?: string[];
    jobStatus?: JobStatuses[];
    createdById?: string;
    haulierId?: string;
    contactedHaulierId?: string;
    query?: string;
    queryBy?: string[];
    assignedToManifest?: boolean;
    archived?: boolean;
    startDate?: string;
    endDate?: string;
    exactDate?: string;
    incotermCode?: string;
}

type Haulier = {
    haulierId: string;
    haulierName: string;
    haulierEmail: string;
    haulierPhone: string;
    status: 'NEW' | 'PROPOSED' | 'ACCEPTED' | 'REJECTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED';
    comments: string;
    proposedDate: string;
    statusChangeDate: string;
};

// We can have either customer or customer.id
export interface JobResponse {
    id: string;
    referenceId: string;
    customerId?: string;
    customer?: Customer;
    brokerId: string;
    manifest?: {
        id: string;
        referenceId: string;
    };
    teamId?: string | null;
    owner?: Individual;
    incotermCode: string;
    cargoId: string;
    description: string;
    specialInstructions: string;
    startDateTime: string;
    endDateTime: string;
    status: JobStatuses;
    creator: Individual;
    createdAt: string;
    tags: string[];
    haulierId: string;
    contactedHauliers: Haulier[];
    archived: boolean;
    deliveryTerms?: string; // INCOTERM
    externalReferences?: {
        ens?: string;
        pbn?: string;
        gmr?: string;
        cdsExportMRN?: string;
    };
    emailStatus?: EmailStatusResponse;
}

export interface DeclarationJobInfo {
    jobId: string;
    jobReferenceId: string;
    declarationType: string;
    importer: string;
    exporter: string;
    declarant: string;
    declarationsSize: number;
}

export type CreateJobPayload = {
    teamId?: string | null;
    incotermCode?: string | null;
    customerId: string;
} & Partial<{
    referenceId: string;
    cargoId: string;
    brokerId: string;
    haulierIds: string[];
    description: string;
    specialInstructions: string;
    startDateTime: string;
    endDateTime: string;
    tags: string[];
}>;

export interface AddJobNotePayload {
    title: string;
    note: string;
}

export interface JobNote {
    id: string;
    jobId: string;
    note: string;
    title: string;
    creator: Individual;
    createdAt: string;
    updatedAt: string;
}

export interface CreateUpdateJobNoteResponse {
    payload: JobNote;
    category: string;
    code: string;
    message: string;
}

export interface GetJobNoteParams {
    sortDirection?: SortDirection;
    page?: number;
    size?: number;
}

export interface GetJobNotesResponse {
    payload: {
        list: JobNote[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
}

export enum JobHistoryTypes {
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    ADDED_TO_MANIFEST = 'ADDED_TO_MANIFEST',
    REMOVED_FROM_MANIFEST = 'REMOVED_FROM_MANIFEST',
    CLEARANCE_AGENT_ASSIGNED = 'CLEARANCE_AGENT_ASSIGNED',
    ARCHIVED = 'ARCHIVED',
    UNARCHIVED = 'UNARCHIVED',
    EXTERNAL_REFERENCES_UPDATED = 'EXTERNAL_REFERENCES_UPDATED',
}

export interface JobHistory {
    id: string;
    jobId: string;
    type: JobHistoryTypes;
    createdAt: string;
    creator: Individual;
}

export interface GetJobHistoryResponse {
    payload: {
        list: JobHistory[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
}

export interface GetJobHistoryParams {
    sortDirection?: SortDirection;
    page: number;
    size: number;
    startDate?: string;
    endDate?: string;
}

export interface GetDocumentUploadUrlParams {
    documentName: string;
    documentSize: number;
}

export interface DocumentPresignedUrlData {
    customerId: string;
    preSignedUrl: string;
    operationType: string;
}

export interface GetDocumentUploadUrlResponse {
    category: string;
    code: string;
    message: string;
    payload: DocumentPresignedUrlData;
}

export interface GetDocumentDownloadUrlResponse {
    category: string;
    code: string;
    message: string;
    payload: DocumentPresignedUrlData;
}

export interface CreateDocumentPayload {
    s3key: string;
    fileName: string;
    fileSize: number;
    type: 'INVOICE' | 'OTHER';
}

export interface JobDocument {
    id: string;
    brokerId: string;
    link: string;
    contentType: string;
    fileName: string;
    fileSize: number;
    securityChecksStatus: SecurityCheckStatus;
    createdAt: string;
    creator: {
        id: string;
        fullName: string;
        phone: string;
        email: string;
        customerId: string;
        vehicleRegistrationNumber: string;
        createdAt: string;
        hasHmrcToken: boolean;
        referenceNumber: string;
        profilePictureLink: string;
        profilePictureStatus: string;
    };
    updatedAt: string;
    archived: boolean;
    archivedAt: string;
}

export interface JobDocumentResponse {
    category: string;
    code: string;
    message: string;
    payload: JobDocument;
}

export type ListDocumentsParams = Partial<{
    page: number;
    size: number;
    sortDirection: SortDirection;
    fileSortParameter: 'ID' | 'FILE_SIZE' | 'CONTENT_TYPE' | 'FILE_NAME' | 'CREATED_AT' | 'UPDATED_AT';
    fileName: string;
    contentType: string[];
    creator: string;
    archived: boolean;
}>;

export type ListDocumentsResponse = {
    category: string;
    code: string;
    message: string;
    payload: ListPayload<JobDocument>;
};

export type UpdateExternalReferencesPayload = {
    ens?: string | null;
    pbn?: string | null;
    gmr?: string | null;
    cdsExportMRN?: string | null;
};
